export type Confidence = 'high' | 'medium' | 'low';

export interface FieldValue<T> {
  value: T;
  confidence: Confidence;
  rawText?: string;
}

export type Cents = number;

export interface LineItem {
  name: FieldValue<string>;
  qty: FieldValue<number>;
  unitPrice?: FieldValue<Cents>;
  total: FieldValue<Cents>;
}

export interface ReceiptMeta {
  sourceFilename: string;
  processedAt: Date;
  ocrEngine: 'tesseract';
  parsedBy: 'rules';
}

export type CategoryCode =
  | 'food'
  | 'transport'
  | 'office'
  | 'healthcare'
  | 'travel'
  | 'utilities'
  | 'entertainment'
  | 'subscriptions'
  | 'personal'
  | 'other';

export interface Receipt {
  vendor: FieldValue<string>;
  date: FieldValue<Date>;
  items: LineItem[];
  subtotal?: FieldValue<Cents>;
  tax?: FieldValue<Cents>;
  tip?: FieldValue<Cents>;
  total: FieldValue<Cents>;
  currency: FieldValue<string>;
  paymentMethod?: FieldValue<string>;
  // User-supplied; not derived from OCR so no FieldValue wrapper. Auto-suggested
  // from a vendor→category map in localStorage when the same vendor was
  // categorised before, OR from the heuristic auto-category inference
  // (vendor table + item keywords). Undefined when the user hasn't picked
  // one and inference returned nothing confident.
  category?: CategoryCode;
  /**
   * Confidence (0-1) of the auto-category inference. Present means the
   * category was set by inference (with this score); the UI shows an
   * "auto · NN%" pill so the user knows it's a guess. Cleared when the
   * user manually picks a category — their choice is by definition
   * authoritative.
   */
  categoryScore?: number;
  /**
   * User-supplied free-text tags. Order is the user's input order.
   * Searched substring-style alongside vendor / item names. Trimmed
   * but not case-folded — the user sees the casing they typed.
   */
  tags?: string[];
  /**
   * User-supplied free-text notes (multi-line allowed). Searched
   * substring-style alongside vendor / item names.
   */
  notes?: string;
  meta: ReceiptMeta;
}

export interface OcrLineBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface OcrLine {
  text: string;
  bbox: OcrLineBox;
  confidence: number;
}

export interface OcrResult {
  text: string;
  lines: OcrLine[];
  meanConfidence: number;
}

export type ProcessingStage =
  | 'load'
  | 'preprocess'
  | 'ocr'
  | 'parse'
  | 'validate'
  | 'done';

export type SessionState =
  | { kind: 'queued' }
  | { kind: 'processing'; stage: ProcessingStage; progress?: number; message?: string }
  | { kind: 'success' }
  | { kind: 'error'; error: string; stage?: ProcessingStage };

export interface DebugEntry {
  /** ms since session start (worker-side performance.now() delta). */
  t: number;
  /** Stage tag — pipeline stage or "scan-correct" sub-step etc. */
  stage: string;
  /** Short human-readable label for what just happened. */
  label: string;
  /** Optional structured payload — must be JSON-serializable for postMessage. */
  data?: unknown;
}

export interface CorrectedImage {
  blob: Blob;
  width: number;
  height: number;
  /** True when scan-correct actually warped; false when it passed through. */
  changed: boolean;
}

export interface ReceiptSession {
  id: string;
  filename: string;
  /**
   * In-batch ordering. Set by enqueue (= sessions.length at insert time)
   * and by the drag-to-reorder action. Persisted to IDB so the order
   * survives reloads when persistence is enabled. Optional only because
   * sessions saved before this field existed don't have it; the storage
   * loader treats missing positions as Number.MAX_SAFE_INTEGER so legacy
   * sessions sort to the end without crashing.
   */
  position: number;
  state: SessionState;
  receipt: Receipt | null;
  ocrDebug: OcrResult | null;
  /** Rolling worker-emitted debug log. Empty unless build-time DEBUG is on. */
  debugLog: DebugEntry[];
  /**
   * JPEG of the scan-correct output (or the pass-through original re-encoded
   * as JPEG when no correction was needed). Always populated post-success —
   * consumed by the DEBUG preview and the v1.3 ZIP batch export.
   */
  correctedImage: CorrectedImage | null;
  /**
   * Quality-assessment findings produced post-validate by the worker. The
   * UI shows a warning banner for `low_quality_photo` (with retake-photo
   * guidance) and otherwise leaves these as a passive metadata channel.
   */
  findings: ReceiptFinding[];
  /**
   * Original uploaded file, kept in memory so the manual-crop modal can
   * re-render the un-warped image. Bounded by the number of sessions on
   * the page; freed when the session is removed. Null only on legacy
   * sessions stored before this field existed (defensive — we don't
   * actually have any persistence here).
   */
  originalFile: File | null;
}

export interface ImagePoint {
  x: number;
  y: number;
}

/**
 * User-supplied receipt corners for manual-crop reprocessing, in the
 * original image's pixel coords. Order is clockwise from the receipt's
 * top-left: [TL, TR, BR, BL]. The CropModal collects these in
 * display-space then scales them back to pixel space before sending.
 */
export type ManualQuad = [ImagePoint, ImagePoint, ImagePoint, ImagePoint];

export type WorkerRequest = {
  kind: 'process';
  id: string;
  file: File;
  lang: string;
  /**
   * When set, skip scan-correct's auto-detection and warp the source
   * image using these four corners instead. Used by the manual-crop
   * fallback for receipts the auto-detector couldn't find (background
   * close to paper brightness, dominant edge isn't the paper boundary).
   */
  manualQuad?: ManualQuad;
};

/**
 * Surfaced to the UI as a warning chip on a session. Mirrors
 * `ValidationFinding` in `workers/pipeline/validate.ts` — duplicated here
 * so lib / UI code doesn't import worker code.
 */
export type ReceiptFinding = {
  kind:
    | 'totals_mismatch'
    | 'date_out_of_range'
    | 'no_items'
    | 'no_total'
    | 'low_quality_photo';
  message: string;
};

export type WorkerResponse =
  | { kind: 'stage'; id: string; stage: ProcessingStage; progress?: number; message?: string }
  | {
      kind: 'result';
      id: string;
      receipt: Receipt;
      ocr: OcrResult;
      findings: ReceiptFinding[];
    }
  | { kind: 'error'; id: string; error: string; stage?: ProcessingStage }
  | { kind: 'debug'; id: string; entry: DebugEntry }
  | {
      kind: 'corrected-image';
      id: string;
      blob: Blob;
      width: number;
      height: number;
      changed: boolean;
    };

export const ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  // ZIPs are unzipped client-side (lib/unzip.ts) and the inner files flow
  // through the regular pipeline. Accepts both common variants browsers
  // report — `application/zip` is the IANA standard, `application/x-zip-compressed`
  // is what older Windows still hands us.
  'application/zip',
  'application/x-zip-compressed',
] as const;

export const ACCEPTED_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.pdf',
  '.zip',
] as const;

// Subset of ACCEPTED_MIME_TYPES that goes through libheif-js in the worker
// instead of the native ImageBitmap path. Kept here as a separate constant so
// pipeline/load.ts and other call sites don't have to know the discriminating
// extensions inline.
export const HEIC_MIME_TYPES = ['image/heic', 'image/heif'] as const;

export interface LangOption {
  code: string;
  label: string;
}

// Languages exposed in the UI. Tesseract supports more (see SUPPORTED_LANGS in
// workers/pipeline/ocr-cache.ts) — add to this list as we extend coverage.
export const LANG_OPTIONS: ReadonlyArray<LangOption> = [
  { code: 'eng', label: 'English' },
  { code: 'spa', label: 'Español' },
  { code: 'fra', label: 'Français' },
  { code: 'deu', label: 'Deutsch' },
  { code: 'nld', label: 'Nederlands' },
  { code: 'ita', label: 'Italiano' },
  { code: 'por', label: 'Português' },
];

export const DEFAULT_LANG = 'eng';
